import { gql, useMutation, useQuery, NetworkStatus } from '@apollo/client'
import { isEmpty, omit } from 'lodash'
import { useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { Content, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewCity } from 'features/Cities/components/NewCity'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { getDifference } from 'utils/object'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { GTTCity } from 'gql/types.generated'
import { GET_CITY } from 'features/Cities/queries-legacy'

const messages = defineMessages({
  helmet: {
    id: 'cities.edit.helmet',
    defaultMessage: 'Edit City | Nexbus Admin'
  }
})

const EDIT_CITY = gql`
  mutation EditCity($city: updateCityInput!) {
    updateCity(input: $city)
  }
`

const EditCityView = (): TSFixMe => {
  const navigate = useNavigate()
  const { cityId } = useParams()
  const intl = useIntl()
  const login = useLogin()

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  let content

  const [editCity, { data: mutationData, loading: mutationLoading, error: mutationError }] = useMutation(EDIT_CITY)

  useEffect(() => {
    if (mutationData) {
      navigate('/cities')
    }
  }, [mutationData])

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery<{ tcity: GTTCity }>(GET_CITY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      cityId
    }
  })

  useEffect(() => {
    refetch()
  }, [])

  if (!canWrite(FEATURES.PLACE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.PLACE} />
  }

  const handleSubmit = (city: TSFixMe): TSFixMe => {
    city = omit(city, ['code'])
    const payload = getDifference(city, queryData?.tcity)

    if (isEmpty(payload)) {
      navigate('/cities')
    } else {
      editCity({
        variables: {
          city: {
            id: cityId,
            ...payload
          }
        }
      })
    }
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="cities.edit.error.description"
              defaultMessage="There was an error while loading your city"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="cities.edit.error.action" defaultMessage="Reload your city" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = (
      <NewCity onSubmit={handleSubmit} city={queryData?.tcity} loading={mutationLoading} error={mutationError} isEdit />
    )
  }

  return (
    <Content hasMaxWidth>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="cities.edit.title" defaultMessage="Edit city" />
        </Heading.H3>
      </ContentHeader>
      <section>
        <Card.Container>{content}</Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(EditCityView)
